import React, { useState } from "react";
import { Container, Row, Col, Form, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addroomType } from "../../store/slices/roomtype";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus } from "react-icons/fa";

export default function AddRoomType() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const MAX_PRICE = 100000;
  const MAX_OCCUPANCY = 8;
  const MAX_EXTRA_BEDS = 10;

  const allowOnlyNumbers = (value) => value.replace(/[^0-9]/g, "");

  const handleLimitedNumberChange = (index, name, value, maxLimit) => {
    let numericValue = allowOnlyNumbers(value);

    if (Number(numericValue) > maxLimit) {
      numericValue = maxLimit.toString();

      setErrors((prev) => ({
        ...prev,
        [name]: `Maximum allowed value is ${maxLimit}`,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    setRoomForms((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [name]: numericValue,
      };

      return updated;
    });
  };

  const handlePriceChange = (index, name, value, meal = null) => {
    let numericValue = allowOnlyNumbers(value);

    if (name === "childBedPrice" && numericValue === "") {
      setErrors((prev) => ({
        ...prev,
        childBedPrice: "Child extra bed price is required",
      }));
    }

    if (Number(numericValue) > MAX_PRICE) {
      numericValue = MAX_PRICE.toString();

      setErrors((prev) => ({
        ...prev,
        [name]: `Maximum allowed amount is ₹${MAX_PRICE}`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    setRoomForms((prev) => {
      const updated = [...prev];

      if (meal) {
        updated[index] = {
          ...updated[index],
          meals: {
            ...updated[index].meals,
            [meal]: {
              ...updated[index].meals[meal],
              price: numericValue,
            },
          },
        };
      } else {
        updated[index] = {
          ...updated[index],
          [name]: numericValue,
        };
      }

      return updated;
    });
  };

  const [form, setForm] = useState({
    name: "",
    maxOccupancy: "",
    basePrice: "",
    extraBedAllowed: false,
    extraBedPrice: "",
    maxExtraBeds: "",
    childBedPrice: "",
    meals: {
      breakfast: { price: "", isIncluded: false },
      lunch: { price: "", isIncluded: false },
      dinner: { price: "", isIncluded: false },
    },
  });

  const [roomForms, setRoomForms] = useState([form]);

  const validateForm = () => {
    let newErrors = {};
    const form = roomForms[0];

    if (!form.name.trim()) {
      newErrors.name = "Please enter room type";
    }

    if (!form.basePrice) {
      newErrors.basePrice = "Please enter base price";
    } else if (isNaN(form.basePrice) || Number(form.basePrice) <= 0) {
      newErrors.basePrice = "Base price must be a valid amount";
    }

    if (!form.maxOccupancy) {
      newErrors.maxOccupancy = "Please enter max occupancy";
    } else if (
      isNaN(form.maxOccupancy) ||
      Number(form.maxOccupancy) <= 0 ||
      Number(form.maxOccupancy) > MAX_OCCUPANCY
    ) {
      newErrors.maxOccupancy = `Max occupancy must be between 1 and ${MAX_OCCUPANCY}`;
    }

    if (form.extraBedAllowed) {
      if (
        !form.maxExtraBeds ||
        isNaN(form.maxExtraBeds) ||
        Number(form.maxExtraBeds) > MAX_EXTRA_BEDS
      ) {
        newErrors.maxExtraBeds = `Max extra beds must be between 1 and ${MAX_EXTRA_BEDS}`;
      }
    }

    ["breakfast", "lunch", "dinner"].forEach((meal) => {
      const price = form.meals[meal].price;

      if (price && isNaN(price)) {
        newErrors[meal] = "Meal price must be numeric";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;

    setRoomForms((prev) => {
      const updatedForms = [...prev];

      updatedForms[index] = {
        ...updatedForms[index],
        [name]: type === "checkbox" ? checked : value,
      };

      return updatedForms;
    });

    // LIVE validation
    if (name === "name") {
      const nameRegex = /^[a-zA-Z0-9][\s\S]*$/;

      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          name: "Please enter room type",
        }));
      } else if (!nameRegex.test(value)) {
        setErrors((prev) => ({
          ...prev,
          name: "Please enter letters first",
        }));
      } else if (value.trim().length < 3) {
        setErrors((prev) => ({
          ...prev,
          name: "Minimum 3 characters required",
        }));
      } else if (value.trim().length > 30) {
        setErrors((prev) => ({
          ...prev,
          name: "Maximum 30 characters allowed",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          name: "",
        }));
      }
    }
  };

  const handleMealChange = (index, meal, field, value) => {
    setRoomForms((prev) => {
      const updatedForms = [...prev];

      updatedForms[index] = {
        ...updatedForms[index],
        meals: {
          ...updatedForms[index].meals,
          [meal]: {
            ...updatedForms[index].meals[meal],
            [field]: value,
          },
        },
      };

      return updatedForms;
    });

    if (errors[meal]) {
      setErrors((prev) => ({ ...prev, [meal]: "" }));
    }
  };

  const handleAddRoomType = () => {
    if (roomForms.length >= 11) {
      toast.error("Maximum 10 room types allowed");
      return;
    }

    setRoomForms((prev) => [
      ...prev,
      {
        name: "",
        maxOccupancy: "",
        basePrice: "",
        extraBedAllowed: false,
        extraBedPrice: "",
        maxExtraBeds: "",
        childBedPrice: "",
        meals: {
          breakfast: { price: "", isIncluded: false },
          lunch: { price: "", isIncluded: false },
          dinner: { price: "", isIncluded: false },
        },
      },
    ]);
  };

  const handleRemoveRoomType = (index) => {
    const updated = [...roomForms];
    updated.splice(index, 1);
    setRoomForms(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      for (const room of roomForms) {
        const payload = {
          hotel: id,
          name: room.name.trim(),
          maxOccupancy: Number(room.maxOccupancy),
          basePrice: Number(room.basePrice),
          extraBedAllowed: room.extraBedAllowed,
          extraBedPrice: room.extraBedAllowed ? Number(room.extraBedPrice) : 0,
          maxExtraBeds: room.extraBedAllowed ? Number(room.maxExtraBeds) : 0,
          childwithBedPrice: Number(room.childBedPrice || 0),
          meals: {
            breakfast: {
              price: Number(room.meals.breakfast.price || 0),
              isIncluded: room.meals.breakfast.isIncluded,
            },
            lunch: {
              price: Number(room.meals.lunch.price || 0),
              isIncluded: room.meals.lunch.isIncluded,
            },
            dinner: {
              price: Number(room.meals.dinner.price || 0),
              isIncluded: room.meals.dinner.isIncluded,
            },
          },
        };

        await dispatch(addroomType(payload)).unwrap();
      }

      toast.success("Rooms added successfully!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error(err || "Failed to add rooms");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/hotel/home" className="breadcrumb-link active">
            Home
          </Link>
          <span className="breadcrumb-separator">|</span>
          <Link onClick={() => navigate(-1)} className="breadcrumb-link active">
            Room Type
          </Link>
          <span className="breadcrumb-separator">|</span>
          <span className="breadcrumb-link inactive">Add Room Type</span>
        </div>
      </div>

      <Container fluid className="p-4">
        <Card className="p-4 border-0">
          <div className="d-flex justify-content-end mt-1">
            <button
              type="button"
              className="my-2 p-2 submit-btn"
              style={{ fontSize: "12px" }}
              onClick={handleAddRoomType}
            >
              <FaPlus size={12} /> Add
            </button>
          </div>

          <Form onSubmit={handleSubmit}>
            {roomForms.map((room, index) => (
              <div key={index} className="border p-3 mb-3 rounded">
                <div className="d-flex justify-content-between mb-2">
                  <h6>Room Type {index + 1}</h6>

                  {index > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveRoomType(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <Row className="mb-3" key={index}>
                  <Col md={4}>
                    <Form.Label>Room Type</Form.Label>
                    <Form.Control
                      name="name"
                      placeholder="Enter room type"
                      value={room.name}
                      onChange={(e) => handleChange(index, e)}
                      minLength={3}
                      maxLength={50}
                    />
                    {errors.name && (
                      <small className="text-danger">{errors.name}</small>
                    )}
                  </Col>

                  <Col md={4}>
                    <Form.Label>Max Occupancy</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter max occupancy"
                      name="maxOccupancy"
                      value={room.maxOccupancy}
                      onChange={(e) =>
                        handleLimitedNumberChange(
                          index,
                          "maxOccupancy",
                          e.target.value,
                          MAX_OCCUPANCY,
                        )
                      }
                    />
                    {errors.maxOccupancy && (
                      <small className="text-danger">
                        {errors.maxOccupancy}
                      </small>
                    )}
                  </Col>

                  <Col md={4}>
                    <Form.Label>Base Price</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter base price"
                      name="basePrice"
                      value={room.basePrice}
                      onChange={(e) =>
                        handlePriceChange(index, "basePrice", e.target.value)
                      }
                    />
                    {errors.basePrice && (
                      <small className="text-danger">{errors.basePrice}</small>
                    )}
                  </Col>
                </Row>

                {/* Extra Bed */}
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Check
                      label="Extra Bed Allowed"
                      name="extraBedAllowed"
                      checked={room.extraBedAllowed}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </Col>

                  {room.extraBedAllowed && (
                    <>
                      <Col md={3}>
                        <Form.Label>Extra Bed Price</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter extra bed price"
                          value={room.extraBedPrice}
                          onChange={(e) =>
                            handlePriceChange(
                              index,
                              "extraBedPrice",
                              e.target.value,
                            )
                          }
                        />
                        {errors.extraBedPrice && (
                          <small className="text-danger">
                            {errors.extraBedPrice}
                          </small>
                        )}
                      </Col>

                      <Col md={3}>
                        <Form.Label>Max Extra Beds</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter max extra beds allowed"
                          value={room.maxExtraBeds}
                          onChange={(e) =>
                            handleLimitedNumberChange(
                              index,
                              "maxExtraBeds",
                              e.target.value,
                              MAX_EXTRA_BEDS,
                            )
                          }
                        />

                        {errors.maxExtraBeds && (
                          <small className="text-danger">
                            {errors.maxExtraBeds}
                          </small>
                        )}
                      </Col>

                      {/* Extra Child Bed Price */}
                      <Col md={3}>
                        <Form.Label>Child With Bed Price</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter child with bed price"
                          value={room.childBedPrice}
                          onChange={(e) =>
                            handlePriceChange(
                              index,
                              "childBedPrice",
                              e.target.value,
                            )
                          }
                        />
                        {errors.childBedPrice && (
                          <small className="text-danger">
                            {errors.childBedPrice}
                          </small>
                        )}
                      </Col>
                    </>
                  )}
                </Row>

                {/* Meals */}
                <h5 className="mt-4 mb-2">Meals</h5>

                {["breakfast", "lunch", "dinner"].map((meal) => (
                  <Row key={meal} className="mb-2 align-items-center">
                    <Col md={3} className="text-capitalize">
                      {meal}
                    </Col>

                    <Col md={3}>
                      <Form.Control
                        type="text"
                        placeholder="Enter price"
                        value={room.meals[meal].price}
                        onChange={(e) =>
                          handlePriceChange(
                            index,
                            "meals",
                            e.target.value,
                            meal,
                          )
                        }
                      />
                    </Col>

                    <Col md={3}>
                      <Form.Check
                        label="Included"
                        checked={room.meals[meal].isIncluded}
                        onChange={(e) =>
                          handleMealChange(
                            index,
                            meal,
                            "isIncluded",
                            e.target.checked,
                          )
                        }
                      />
                    </Col>

                    {errors[meal] && (
                      <small className="text-danger">{errors[meal]}</small>
                    )}
                  </Row>
                ))}
              </div>
            ))}

            <Row className="mt-5 mb-5">
              <Col className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Container>
    </>
  );
}
